import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  activateMilestone,
  createMilestone,
  deactivateMilestone,
  fetchMilestones,
  updateMilestone,
  type CreateMilestoneInput,
  type UpdateMilestoneInput,
} from '../api/milestone.api';

import { ISSUE_KEYS } from './useIssues';

export const MILESTONE_KEYS = {
  list: (projectId: string) => ['milestones', projectId] as const,
};

export function useMilestones(projectId: string, enabled = true) {
  return useQuery({
    queryKey: MILESTONE_KEYS.list(projectId),
    queryFn: () => fetchMilestones(projectId),
    enabled: Boolean(projectId) && enabled,
  });
}

export function useCreateMilestone(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMilestoneInput) => createMilestone(projectId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MILESTONE_KEYS.list(projectId) }),
  });
}

export function useUpdateMilestone(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ milestoneId, data }: { milestoneId: string; data: UpdateMilestoneInput }) =>
      updateMilestone(projectId, milestoneId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MILESTONE_KEYS.list(projectId) }),
  });
}

export function useDeactivateMilestone(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ milestoneId, dissociateIssues }: { milestoneId: string; dissociateIssues: boolean }) =>
      deactivateMilestone(projectId, milestoneId, dissociateIssues),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MILESTONE_KEYS.list(projectId) });
      void queryClient.invalidateQueries({ queryKey: ISSUE_KEYS.list(projectId) });
    },
  });
}

export function useActivateMilestone(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (milestoneId: string) => activateMilestone(projectId, milestoneId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MILESTONE_KEYS.list(projectId) }),
  });
}
